import { Controller } from "@hotwired/stimulus"
import axios from "axios"
import debounce from "lodash/debounce"
import { EnhancedSelect, EnhancedSelectItem } from "../../utils/enhanced_select"
import { LucideIconCache, Users, User } from "../../utils/lucide_icon_cache"

interface RecipientItem extends EnhancedSelectItem {
  id: string
  value: number | string
  label: string
  type: "group" | "client" | "recent_client"
  count?: number
}

export default class UnifiedRecipientsController extends Controller {
  declare inputTarget: HTMLSelectElement
  declare hiddenFieldsTarget: HTMLElement
  declare excludeSectionTarget: HTMLElement
  declare excludeListTarget: HTMLElement

  declare groupIdsValue: number[]
  declare clientIdsValue: number[]

  private enhancedSelect: EnhancedSelect | null = null
  private debouncedLoadExcludeList = debounce(() => this.performLoadExcludeList(), 300)
  private debouncedReloadWithExclusions = debounce(() => this.reloadWithExclusions(), 300)

  private getIconSvg(type: "group" | "client" | "recent_client", size: 14 | 16): string {
    const icon = type === "group" ? Users : User
    return LucideIconCache.get(icon, size, "recipient-icon")
  }

  static targets = ["input", "hiddenFields", "excludeSection", "excludeList"]

  static values = {
    groupIds: { type: Array, default: [] },
    clientIds: { type: Array, default: [] }
  }

  inputTargetConnected(select: HTMLSelectElement) {
    this.enhancedSelect = new EnhancedSelect(select, {
      placeholder: "Search groups or contacts...",
      maxItems: null,
      closeOnSelect: true,
      openOnFocus: false,
      valueField: "id",
      labelField: "label",
      disableSearchFiltering: true, // Search is server-side
      disableQueryCache: true, // Force fresh server fetch on every query
      optgroupField: "type",
      optgroups: [
        { value: "group", label: "Groups" },
        { value: "client", label: "Contacts" },
        { value: "recent_client", label: "Recent contacts" }
      ],
      load: (query, signal) => this.searchRecipients(query, signal),
      loadThrottle: 300,
      shouldLoad: () => true, // Always load, including empty query for recent contacts
      preload: "focus",
      render: {
        optgroup_header: (data) => {
          return `<div class="optgroup-header">${data.label || ""}</div>`
        },
        option: (data) => this.renderOption(data as unknown as RecipientItem),
        item: (data) => this.renderItem(data as unknown as RecipientItem)
      },
      onChange: () => this.handleSelectionChange()
    })

    // Pre-populate with existing selections if any
    this.loadInitialSelections()

    // Dispatch initial state for wizard footer
    this.dispatchRecipientsChanged()
  }

  inputTargetDisconnected() {
    if (this.enhancedSelect) {
      this.enhancedSelect.destroy()
      this.enhancedSelect = null
    }
  }

  onExclusionChange() {
    this.debouncedReloadWithExclusions()
  }

  groupIdsValueChanged() {
    this.syncHiddenFields()
    this.updateExcludeVisibility()
    this.dispatchRecipientsChanged()
  }

  clientIdsValueChanged() {
    this.syncHiddenFields()
    this.dispatchRecipientsChanged()
  }

  private loadInitialSelections() {
    const groupIds = this.groupIdsValue
    const clientIds = this.clientIdsValue

    if (groupIds.length === 0 && clientIds.length === 0) return

    // TomSelect automatically discovers <option> elements from HTML
    // Just set the values - options are already loaded
    const values = [
      ...groupIds.map((id) => `group_${id}`),
      ...clientIds.map((id) => `client_${id}`)
    ]

    this.enhancedSelect?.setValue(values, true)
    this.updateExcludeVisibility()
    if (groupIds.length > 0 || clientIds.length > 0) {
      this.performLoadExcludeList()
    }
  }

  private async searchRecipients(query: string, signal: AbortSignal): Promise<RecipientItem[]> {
    // When no query, show recent clients with "recent_client" type for different header
    if (!query || query.length < 1) {
      const response = await axios.get<RecipientItem[]>("/clients/recent.json", { signal })
      return response.data.map((client) => ({
        ...client,
        type: "recent_client" as const
      }))
    }

    const encodedQuery = encodeURIComponent(query)
    const [groupsResponse, clientsResponse] = await Promise.all([
      axios.get<RecipientItem[]>(`/groups/search.json?q=${encodedQuery}`, { signal }),
      axios.get<RecipientItem[]>(`/clients/search.json?q=${encodedQuery}`, { signal })
    ])

    return [...groupsResponse.data, ...clientsResponse.data]
  }

  private renderOption(data: RecipientItem): string {
    const iconSvg = this.getIconSvg(data.type, 16)
    const count = data.count !== undefined ? ` (${data.count})` : ""
    const label = this.createSafeTextContent(data.label)

    return `<div class="recipient-option recipient-option--${data.type}">
      <span class="recipient-option-icon">${iconSvg}</span>
      <span class="recipient-option-label">${label}${count}</span>
    </div>`
  }

  private renderItem(data: RecipientItem): string {
    const iconSvg = this.getIconSvg(data.type, 14)
    const label = this.createSafeTextContent(data.label)

    return `<div class="select-tag select-tag--${data.type}">
      <span class="select-tag-icon">${iconSvg}</span>
      <span>${label}</span>
    </div>`
  }

  private createSafeTextContent(str: string): string {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  private handleSelectionChange() {
    if (!this.enhancedSelect) return

    const items = this.enhancedSelect.getItems<RecipientItem>()

    this.groupIdsValue = items
      .filter((item) => item.type === "group")
      .map((item) => this.extractNumericId(item))

    this.clientIdsValue = items
      .filter((item) => item.type === "client" || item.type === "recent_client")
      .map((item) => this.extractNumericId(item))

    this.debouncedLoadExcludeList()
  }

  private extractNumericId(item: RecipientItem): number {
    if (typeof item.value === "number") {
      return item.value
    }

    const idStr = String(item.id || item.value)
    const digits = idStr.match(/(?:group|client)_(\d+)/)?.[1] ?? idStr
    const numericId = parseInt(digits, 10)

    if (Number.isNaN(numericId)) {
      throw new Error(`Unrecognised recipient id: ${idStr}`)
    }

    return numericId
  }

  private syncHiddenFields() {
    this.hiddenFieldsTarget.replaceChildren(
      ...this.groupIdsValue.map((id) => this.createHiddenInput("message_draft[group_ids][]", id)),
      ...this.clientIdsValue.map((id) => this.createHiddenInput("message_draft[client_ids][]", id))
    )
  }

  private createHiddenInput(name: string, value: number): HTMLInputElement {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = String(value)
    return input
  }

  private updateExcludeVisibility() {
    const hasGroups = this.groupIdsValue.length > 0
    this.excludeSectionTarget.style.display = hasGroups ? "block" : "none"
  }

  private performLoadExcludeList() {
    const groupIds = this.groupIdsValue
    const clientIds = this.clientIdsValue

    if (groupIds.length === 0 && clientIds.length === 0) {
      this.excludeListTarget.removeAttribute("src")
      return
    }

    const params = [
      ...groupIds.map((id) => `group_ids[]=${id}`),
      ...clientIds.map((id) => `client_ids[]=${id}`)
    ].join("&")

    this.excludeListTarget.setAttribute("src", `/recipients?${params}`)
  }

  private reloadWithExclusions() {
    const groupIds = this.groupIdsValue
    const clientIds = this.clientIdsValue

    if (groupIds.length === 0 && clientIds.length === 0) {
      this.excludeListTarget.removeAttribute("src")
      return
    }

    const excludedIds = this.getExcludedClientIds()

    const params = [
      ...groupIds.map((id) => `group_ids[]=${id}`),
      ...clientIds.map((id) => `client_ids[]=${id}`),
      ...excludedIds.map((id) => `excluded_client_ids[]=${id}`)
    ].join("&")

    this.excludeListTarget.setAttribute("src", `/recipients?${params}`)
  }

  private getExcludedClientIds(): number[] {
    const checkboxes = this.excludeListTarget.querySelectorAll(
      'input[name="message_draft[excluded_client_ids][]"]:checked'
    )
    return Array.from(checkboxes).map((cb) => parseInt((cb as HTMLInputElement).value, 10))
  }

  private dispatchRecipientsChanged() {
    const hasRecipients = this.groupIdsValue.length > 0 || this.clientIdsValue.length > 0
    this.dispatch("recipientsChanged", {
      detail: { hasRecipients },
      prefix: "compose"
    })
  }
}
